import { _objectAssign } from '@naturalcycles/js-lib'
import { Experiment } from '@naturalcycles/shared'
import { QuizSignatureObject } from '@src/app/core/services/quiz/quiz.service.js'
import { QuizModifier } from '@src/app/core/util/quiz.util.js'
import { AnyPage, Page } from '@src/app/shared/typings/enum/pages.js'
import { Breakpoint, Sections } from '@src/app/shared/typings/enum/quiz.js'
import { SignupQuizFlow, WSQuizEngineConfig } from '@src/app/shared/typings/interfaces/quiz.js'
import {
  Ab355QuizDataInput,
  cycleChangesCervicalMucusAb355Test1BreakpointAction,
  cycleChangesCervicalMucusAb355Test2BreakpointAction,
  cycleChangesMoodAb355Test1BreakpointAction,
  cycleChangesMoodAb355Test2BreakpointAction,
  cycleChangesPainAb355AnyTestBreakpointAction,
  cycleChangesSexDriveAb355Test1BreakpointAction,
  cycleChangesSexDriveAb355Test2BreakpointAction,
  cycleChangesSkinAb355AnyTestBreakpointAction,
  getAb355Test2MedicalConditionsBreakpointAction,
  getAb355TestMedicalConditionsBreakpointAction,
} from './ab355.js'

class Exp355Quiz implements QuizModifier<WSQuizEngineConfig> {
  public _shouldEnhance(obj: QuizSignatureObject): boolean {
    return obj.experiment[Experiment.WS_CYCLE_INSIGHTS]?.includes('test') ?? false
  }

  public enhanceEngineConfig(
    obj: QuizSignatureObject,
    config: WSQuizEngineConfig,
  ): WSQuizEngineConfig {
    if (!this._shouldEnhance(obj)) return config
    const bucket = obj.experiment[Experiment.WS_CYCLE_INSIGHTS]
    _objectAssign(config.guardData, {
      [Page.cycleChangesMood]: data => !!data?.medicalConditions,
      [Page.cycleChangesPainExperience]: data => !!(data as Ab355QuizDataInput)?.cycleChangesMood,
      [Page.cycleChangesSexDrive]: data => !!(data as Ab355QuizDataInput)?.cycleChangesPain,
      [Page.cycleChangesMucus]: data => !!(data as Ab355QuizDataInput)?.cycleChangesSexDrive,
      [Page.cycleChangesSkin]: data => !!(data as Ab355QuizDataInput)?.cycleChangesCervicalMucus,
      [Page.cycleChangesThankYou]: data => !!(data as Ab355QuizDataInput)?.cycleChangesSkin,
      [Page.naturalMethod]: data => !!(data as Ab355QuizDataInput)?.cycleChangesSkin,
      [Page.yourMeasuringDevice]: data => !!(data as Ab355QuizDataInput)?.cycleChangesSkin,
      [Page.whichWearable]: data => !!(data as Ab355QuizDataInput)?.cycleChangesSkin,
    })
    if (bucket === 'test') {
      _objectAssign(config.breakpointActions, {
        [Breakpoint.medicalConditions]: getAb355TestMedicalConditionsBreakpointAction(
          config.breakpointActions[Breakpoint.medicalConditions],
        ),
        [Breakpoint.cycleChangesMood]: cycleChangesMoodAb355Test1BreakpointAction,
        [Breakpoint.cycleChangesPain]: cycleChangesPainAb355AnyTestBreakpointAction,
        [Breakpoint.cycleChangesSexDrive]: cycleChangesSexDriveAb355Test1BreakpointAction,
        [Breakpoint.cycleChangesCervicalMucus]: cycleChangesCervicalMucusAb355Test1BreakpointAction,
        [Breakpoint.cycleChangesSkin]: cycleChangesSkinAb355AnyTestBreakpointAction,
      })
    } else {
      _objectAssign(config.breakpointActions, {
        [Breakpoint.medicalConditions]: getAb355Test2MedicalConditionsBreakpointAction(
          config.breakpointActions[Breakpoint.medicalConditions],
        ),
        [Breakpoint.cycleChangesMood]: cycleChangesMoodAb355Test2BreakpointAction,
        [Breakpoint.cycleChangesPain]: cycleChangesPainAb355AnyTestBreakpointAction,
        [Breakpoint.cycleChangesSexDrive]: cycleChangesSexDriveAb355Test2BreakpointAction,
        [Breakpoint.cycleChangesCervicalMucus]: cycleChangesCervicalMucusAb355Test2BreakpointAction,
        [Breakpoint.cycleChangesSkin]: cycleChangesSkinAb355AnyTestBreakpointAction,
      })
    }

    return config
  }

  public enhanceSections(obj: QuizSignatureObject, sections: Sections): Sections {
    if (!this._shouldEnhance(obj)) return sections
    return sections
  }

  public enhanceInitialFlow(obj: QuizSignatureObject, flow: SignupQuizFlow): SignupQuizFlow {
    if (!this._shouldEnhance(obj)) return flow
    return flow
  }

  public enhanceEntry(obj: QuizSignatureObject, entry: AnyPage[] | null): AnyPage[] | null {
    if (!this._shouldEnhance(obj)) return entry
    return entry
  }

  public enhanceExit(obj: QuizSignatureObject, exit: AnyPage[] | null): AnyPage[] | null {
    if (!this._shouldEnhance(obj)) return exit
    return exit
  }
}

export const exp355Quiz = new Exp355Quiz()
